import React, { useEffect, useState } from 'react';
import { View, Text, Button } from "../../../Components/Themed";
import { ActivityIndicator, Modal, ScrollView, TouchableOpacity } from 'react-native';
import { useColors } from '../../../constants/useColors';
import { getPartners, getContests, getParticipants } from '../../api';
import { BetRouterApi } from '../../api';
import { SearchBox } from '../../../Components';
import Pagination from '../../../Components/Pagination';
import type {
    RouterLeagueProps,
    RouterPartnerProps,
    RouterContestProps,
    RouterParticipantProps,
    //PartnerContestProps,
    PartnerLeagueProps,
    RawLeagueProps
} from '../../types';

type LeagueInfoProps = {
    league: RouterLeagueProps;
};

const LeagueInfo = ({ league }: LeagueInfoProps) => {
    const Colors = useColors();

    const [loading, setLoading] = useState(true);
    const [partners, setPartners] = useState<RouterPartnerProps[]>([]);
    const [partnerLeagues, setPartnerLeagues] = useState<PartnerLeagueProps[]>([]);
    const [rawLeagues, setRawLeagues] = useState<RawLeagueProps[]>([]);
    const [contests, setContests] = useState<RouterContestProps[]>([]);
    const [participants, setParticipants] = useState<RouterParticipantProps[]>([]);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState<RouterPartnerProps | null>(null);
    const [selectedRawLeagues, setSelectedRawLeagues] = useState<string[]>([]);
    const [linkModalSearch, setLinkModalSearch] = useState('');
    const [linkModalPage, setLinkModalPage] = useState(0);
    const ITEMS_PER_PAGE = 10;

    useEffect(() => {
        loadLeagueData();
    }, [league.router_league_id]);

    const loadLeagueData = async () => {
        setLoading(true);
        try {
            // Load all necessary data in parallel - only get open/active contests
            const [partnersRes, leaguesDataRes, contestsRes, participantsRes] = await Promise.all([
                getPartners(),
                BetRouterApi.BetRouter.LeagueApi.getOpenLeagues(),
                getContests('open'),
                getParticipants(league.router_league_id)
            ]);

            // All these endpoints return arrays directly according to api-contracts.ts
            if (Array.isArray(partnersRes)) setPartners(partnersRes);

            // Filter partner leagues for this specific router league
            if (leaguesDataRes && leaguesDataRes.partner_leagues) {
                const filteredPartnerLeagues = leaguesDataRes.partner_leagues.filter(pl => pl.router_league_id === league.router_league_id);
                console.log('All partner_leagues:', leaguesDataRes.partner_leagues);
                console.log('Filtered for league', league.router_league_id, ':', filteredPartnerLeagues);
                setPartnerLeagues(filteredPartnerLeagues);
            }

            // Store raw leagues
            if (leaguesDataRes && leaguesDataRes.raw_leagues) {
                setRawLeagues(leaguesDataRes.raw_leagues);
            }

            if (Array.isArray(contestsRes)) {
                setContests(contestsRes.filter(c => c.router_league_id === league.router_league_id));
            }
            if (Array.isArray(participantsRes)) setParticipants(participantsRes);
        } catch (error) {
            console.error('Failed to load league data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadRouterContests = async () => {
        try {
            setLoading(true);
            const result = await BetRouterApi.BetRouter.ContestApi.loadContests(league.router_league_id);
            alert(`Successfully loaded ${result.count} router contests for ${league.name}`);
            // Reload the league data to show updated counts
            await loadLeagueData();
        } catch (error) {
            console.error('Failed to load router contests:', error);
            alert('Error loading router contests: ' + (error as any).message);
            setLoading(false);
        }
    };

    const openLinkModal = (partner: RouterPartnerProps) => {
        setSelectedPartner(partner);
        setShowLinkModal(true);
        setSelectedRawLeagues([]);
        setLinkModalSearch('');
        setLinkModalPage(0);
    };

    const handleLinkLeagues = async () => {
        if (!selectedPartner || selectedRawLeagues.length === 0) {
            alert('Please select at least one league to link');
            return;
        }

        try {
            setLoading(true);
            const result = await BetRouterApi.BetRouter.LeagueApi.linkLeague(
                selectedRawLeagues,
                league.router_league_id
            );
            alert(`Successfully linked ${result.partnerLeagues.length} league(s) for ${selectedPartner.name}`);
            setShowLinkModal(false);
            setSelectedRawLeagues([]);
            // Reload to show updated counts
            await loadLeagueData();
        } catch (error) {
            console.error('Failed to link leagues:', error);
            alert('Error linking leagues: ' + (error as any).message);
            setLoading(false);
        }
    };

    const toggleRawLeagueSelection = (rawLeagueId: string) => {
        setSelectedRawLeagues(prev => {
            if (prev.includes(rawLeagueId)) {
                return prev.filter(id => id !== rawLeagueId);
            } else {
                return [...prev, rawLeagueId];
            }
        });
    };

    if (loading) {
        return (
            <View transparent style={{ padding: 40, alignItems: 'center' }}>
                <ActivityIndicator size="large" color={Colors.text.h1} />
            </View>
        );
    }

    // Calculate stats
    const activeParticipants = participants.filter(p => p.status === 'active').length;
    const teamCount = participants.filter(p => p.participant_type === 'team').length;
    const athleteCount = participants.filter(p => p.participant_type === 'athlete').length;

    return (
        <View transparent>
            {/* Header */}
            <View transparent style={{ marginBottom: 24, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View transparent>
                    <Text theme='h1' size={24}>{league.name}</Text>
                    <Text theme='description' size={14} style={{ marginTop: 8 }}>
                        League Overview
                    </Text>
                </View>
                <Button
                    type='success'
                    title='Load Router Contests'
                    onPress={loadRouterContests}
                />
            </View>

            {/* Stats Grid */}
            <View transparent style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
                {/* Contests Card */}
                <View type='body' style={{ flex: 1, minWidth: 200, padding: 16, borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light }}>
                    <Text theme='description' size={12}>CONTESTS</Text>
                    <Text theme='h1' size={32} style={{ marginTop: 8 }}>{contests.length}</Text>
                    <Text theme='description' size={11} style={{ marginTop: 4 }}>
                        Total router contests
                    </Text>
                </View>

                {/* Participants Card */}
                <View type='body' style={{ flex: 1, minWidth: 200, padding: 16, borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light }}>
                    <Text theme='description' size={12}>PARTICIPANTS</Text>
                    <Text theme='h1' size={32} style={{ marginTop: 8 }}>{activeParticipants}</Text>
                    <Text theme='description' size={11} style={{ marginTop: 4 }}>
                        {teamCount} teams, {athleteCount} athletes
                    </Text>
                </View>

                {/* Partners Card */}
                <View type='body' style={{ flex: 1, minWidth: 200, padding: 16, borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light }}>
                    <Text theme='description' size={12}>PARTNERS</Text>
                    <Text theme='h1' size={32} style={{ marginTop: 8 }}>
                        {new Set(partnerLeagues.map(pl => pl.partner_id)).size}
                    </Text>
                    <Text theme='description' size={11} style={{ marginTop: 4 }}>
                        Connected to this league
                    </Text>
                </View>
            </View>

            {/* Partner League Connection Status */}
            <View type='body' style={{ borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light, overflow: 'hidden' }}>
                <View type='header' style={{ padding: 16 }}>
                    <Text theme='h2' size={16}>Partner League Connections</Text>
                    <Text theme='description' size={12} style={{ marginTop: 4 }}>
                        Raw league mappings for this router league
                    </Text>
                </View>

                <View transparent style={{ padding: 16 }}>
                    {partners.map(partner => {
                        const partnerLeaguesForPartner = partnerLeagues.filter(pl => pl.partner_id === partner.partner_id);
                        const leagueCount = partnerLeaguesForPartner.length;
                        const isConnected = leagueCount > 0;
                        console.log(`Partner ${partner.name} (${partner.partner_id}):`, {
                            partnerLeaguesForPartner,
                            leagueCount,
                            isConnected,
                            allPartnerLeagues: partnerLeagues
                        });

                        return (
                            <View
                                key={partner.partner_id}
                                transparent
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: 12,
                                    backgroundColor: Colors.views.header,
                                    borderRadius: 6,
                                    marginBottom: 8,
                                }}
                            >
                                <View transparent style={{ flex: 1 }}>
                                    <Text theme='h2' size={14}>{partner.name}</Text>
                                    {isConnected && (
                                        <Text theme='description' size={11} style={{ marginTop: 2 }}>
                                            {leagueCount} raw league{leagueCount !== 1 ? 's' : ''} linked
                                        </Text>
                                    )}
                                </View>

                                <View transparent style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    <View
                                        transparent
                                        style={{
                                            paddingHorizontal: 12,
                                            paddingVertical: 6,
                                            borderRadius: 12,
                                            backgroundColor: isConnected ? Colors.text.success : Colors.views.header,
                                        }}
                                    >
                                        <Text
                                            theme='description'
                                            size={11}
                                            style={{ color: isConnected ? '#fff' : Colors.text.descriptionLight }}
                                        >
                                            {isConnected ? '✓ Connected' : 'Not Connected'}
                                        </Text>
                                    </View>
                                    <Button
                                        type='action'
                                        title='Link'
                                        onPress={() => openLinkModal(partner)}
                                        style={{ paddingHorizontal: 12, paddingVertical: 6 }}
                                    />
                                </View>
                            </View>
                        );
                    })}
                </View>
            </View>

            {/* League Details */}
            <View type='body' style={{ marginTop: 16, borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light, overflow: 'hidden' }}>
                <View type='header' style={{ padding: 16 }}>
                    <Text theme='h2' size={16}>League Details</Text>
                </View>

                <View transparent style={{ padding: 16 }}>
                    <View transparent style={{ flexDirection: 'row', marginBottom: 12 }}>
                        <Text theme='description' size={13} style={{ width: 140 }}>League ID:</Text>
                        <Text theme='h2' size={13} style={{ flex: 1 }}>{league.router_league_id}</Text>
                    </View>

                    <View transparent style={{ flexDirection: 'row', marginBottom: 12 }}>
                        <Text theme='description' size={13} style={{ width: 140 }}>Name:</Text>
                        <Text theme='h2' size={13} style={{ flex: 1 }}>{league.name}</Text>
                    </View>

                    <View transparent style={{ flexDirection: 'row', marginBottom: 12 }}>
                        <Text theme='description' size={13} style={{ width: 140 }}>Status:</Text>
                        <Text theme='h2' size={13} style={{ flex: 1 }}>
                            {league.status.charAt(0).toUpperCase() + league.status.slice(1)}
                        </Text>
                    </View>

                    <View transparent style={{ flexDirection: 'row' }}>
                        <Text theme='description' size={13} style={{ width: 140 }}>Created:</Text>
                        <Text theme='h2' size={13} style={{ flex: 1 }}>
                            {new Date(league.create_datetime).toLocaleDateString()}
                        </Text>
                    </View>
                </View>
            </View>

            {/* Link Leagues Modal */}
            <Modal
                visible={showLinkModal}
                transparent
                animationType="fade"
                onRequestClose={() => setShowLinkModal(false)}
            >
                <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
                    <View style={{ backgroundColor: Colors.views.body, borderRadius: 12, padding: 24, width: '90%', maxWidth: 600 }}>
                        <Text theme="h1" size={20} style={{ marginBottom: 12 }}>
                            Link Leagues - {selectedPartner?.name}
                        </Text>
                        <Text theme="description" size={14} style={{ marginBottom: 16 }}>
                            Select raw leagues from {selectedPartner?.name} to link to {league.name}
                        </Text>

                        {/* Search Box */}
                        <View transparent style={{ marginBottom: 16 }}>
                            <SearchBox
                                placeholder="Search leagues..."
                                onChange={(text) => {
                                    setLinkModalSearch(text);
                                    setLinkModalPage(0); // Reset to first page on search
                                }}
                                hide_search_button
                            />
                        </View>

                        {(() => {
                            // Filter raw leagues by selected partner
                            let partnerRawLeagues = rawLeagues.filter(rl => rl.partner_id === selectedPartner?.partner_id);

                            // Apply search filter
                            if (linkModalSearch) {
                                partnerRawLeagues = partnerRawLeagues.filter(rl =>
                                    rl.name.toLowerCase().includes(linkModalSearch.toLowerCase()) ||
                                    rl.external_id.toLowerCase().includes(linkModalSearch.toLowerCase())
                                );
                            }

                            if (partnerRawLeagues.length === 0) {
                                return (
                                    <View transparent style={{ padding: 40, alignItems: 'center' }}>
                                        <Text theme="description" size={14}>
                                            {linkModalSearch
                                                ? `No leagues found matching "${linkModalSearch}"`
                                                : `No raw leagues found for ${selectedPartner?.name}. Try loading raw leagues first from the partner.`
                                            }
                                        </Text>
                                    </View>
                                );
                            }

                            // Calculate pagination
                            const totalPages = Math.ceil(partnerRawLeagues.length / ITEMS_PER_PAGE);
                            const startIndex = linkModalPage * ITEMS_PER_PAGE;
                            const endIndex = startIndex + ITEMS_PER_PAGE;
                            const paginatedLeagues = partnerRawLeagues.slice(startIndex, endIndex);

                            return (
                                <>
                                    <ScrollView style={{ maxHeight: 400 }}>
                                        <View transparent style={{ gap: 8 }}>
                                            {paginatedLeagues.map((rawLeague) => {
                                        const isSelected = selectedRawLeagues.includes(rawLeague.raw_league_id);
                                        const isAlreadyLinked = partnerLeagues.some(
                                            pl => pl.raw_league_id === rawLeague.raw_league_id && pl.router_league_id === league.router_league_id
                                        );

                                        return (
                                            <TouchableOpacity
                                                key={rawLeague.raw_league_id}
                                                onPress={() => !isAlreadyLinked && toggleRawLeagueSelection(rawLeague.raw_league_id)}
                                                disabled={isAlreadyLinked}
                                                style={{
                                                    padding: 12,
                                                    backgroundColor: isAlreadyLinked ? Colors.views.header : (isSelected ? Colors.views.body : Colors.views.header),
                                                    borderRadius: 6,
                                                    borderWidth: 2,
                                                    borderColor: isAlreadyLinked ? Colors.borders.light : (isSelected ? Colors.views.body : Colors.borders.light),
                                                    opacity: isAlreadyLinked ? 0.5 : 1,
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}
                                            >
                                                <View transparent style={{ flex: 1 }}>
                                                    <Text theme="h2" size={14}>
                                                        {rawLeague.name}
                                                    </Text>
                                                    <Text theme="description" size={11} style={{ marginTop: 2 }}>
                                                        {rawLeague.external_id}
                                                    </Text>
                                                </View>
                                                {isAlreadyLinked && (
                                                    <View
                                                        transparent
                                                        style={{
                                                            paddingHorizontal: 8,
                                                            paddingVertical: 4,
                                                            borderRadius: 4,
                                                            backgroundColor: Colors.text.success
                                                        }}
                                                    >
                                                        <Text theme="h2" size={10} style={{ color: '#fff' }}>
                                                            Already Linked
                                                        </Text>
                                                    </View>
                                                )}
                                                {isSelected && !isAlreadyLinked && (
                                                    <View
                                                        transparent
                                                        style={{
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: 10,
                                                            backgroundColor: '#fff',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        <Text theme="h2" size={12}>✓</Text>
                                                    </View>
                                                )}
                                            </TouchableOpacity>
                                        );
                                            })}
                                        </View>
                                    </ScrollView>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <View transparent style={{ marginTop: 16 }}>
                                            <Pagination
                                                offset={linkModalPage}
                                                pages={totalPages}
                                                onPrevious={() => setLinkModalPage(prev => Math.max(0, prev - 1))}
                                                onNext={() => setLinkModalPage(prev => Math.min(totalPages - 1, prev + 1))}
                                                onSelectPage={(page) => setLinkModalPage(page)}
                                            />
                                        </View>
                                    )}

                                    {/* Results count */}
                                    <View transparent style={{ marginTop: 8, alignItems: 'center' }}>
                                        <Text theme="description" size={12}>
                                            Showing {startIndex + 1}-{Math.min(endIndex, partnerRawLeagues.length)} of {partnerRawLeagues.length} league(s)
                                        </Text>
                                    </View>
                                </>
                            );
                        })()}

                        <View transparent style={{ flexDirection: 'row', gap: 12, marginTop: 24 }}>
                            <Button
                                type="close"
                                title="Cancel"
                                onPress={() => setShowLinkModal(false)}
                                style={{ flex: 1 }}
                            />
                            <Button
                                type="success"
                                title={`Link ${selectedRawLeagues.length} League(s)`}
                                onPress={handleLinkLeagues}
                                style={{ flex: 1 }}
                            />
                        </View>
                    </View>
                </View>
            </Modal>
        </View>
    );
};

export default LeagueInfo;
